import React, {useEffect, useRef, useState} from 'react';
import AlertModal from '@/Components/AlertModal';

const HIGH_RTT_THRESHOLD_MS = 300;
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const MAX_NOTIFICATIONS = 2;
const SLOW_EFFECTIVE_TYPES = ['slow-2g', '2g', '3g'];

function readConnection(): any {
  const nav = navigator as any;
  return nav.connection || nav.mozConnection || nav.webkitConnection || null;
}

function isNetworkPoor(): boolean {
  const connection = readConnection();
  if (!connection) return false;
  const slowType = SLOW_EFFECTIVE_TYPES.includes(connection.effectiveType);
  const highRtt =
    typeof connection.rtt === 'number' && connection.rtt > HIGH_RTT_THRESHOLD_MS;
  return slowType || highRtt;
}

type NetworkQualityMonitorProps = {
  intervalMs?: number;
  maxNotifications?: number;
  header?: string;
  bodyTxt?: string;
};

function NetworkQualityMonitor({
  intervalMs = POLL_INTERVAL_MS,
  maxNotifications = MAX_NOTIFICATIONS,
  header = 'Network unstable',
  bodyTxt = 'Your network appears unstable, which may affect video and audio quality.',
}: NetworkQualityMonitorProps) {
  const [open, setOpen] = useState(false);
  const shownCountRef = useRef(0);
  const wasPoorRef = useRef(false);
  const openRef = useRef(false);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    const describe = () => {
      const c = readConnection();
      return c
        ? `effectiveType=${c.effectiveType}, rtt=${c.rtt}ms, downlink=${c.downlink}`
        : 'no Network Information API';
    };

    const tryNotify = (trigger: string) => {
      const poor = isNetworkPoor();
      const wasPoor = wasPoorRef.current;
      const transitionedToPoor = poor && !wasPoor;
      wasPoorRef.current = poor;

      console.log(
        `[NetworkQualityMonitor] check (${trigger}): ${describe()} | poor=${poor} wasPoor=${wasPoor} transition=${transitionedToPoor} shown=${shownCountRef.current}/${maxNotifications} open=${openRef.current}`,
      );

      if (!transitionedToPoor) return;
      if (shownCountRef.current >= maxNotifications) {
        console.log('[NetworkQualityMonitor] suppressed: cap reached');
        return;
      }
      if (openRef.current) {
        console.log('[NetworkQualityMonitor] suppressed: modal already open');
        return;
      }

      shownCountRef.current += 1;
      console.log(
        `[NetworkQualityMonitor] showing modal (#${shownCountRef.current})`,
      );
      setOpen(true);
    };

    tryNotify('mount');
    const id = setInterval(() => tryNotify('interval'), intervalMs);

    (window as any).__networkQualityMonitor = {
      check: () => tryNotify('manual'),
      state: () => ({
        wasPoor: wasPoorRef.current,
        shown: shownCountRef.current,
        cap: maxNotifications,
        open: openRef.current,
        connection: describe(),
      }),
      reset: () => {
        wasPoorRef.current = false;
        shownCountRef.current = 0;
        console.log('[NetworkQualityMonitor] state reset');
      },
    };
    console.log(
      '[NetworkQualityMonitor] mounted. Debug API: window.__networkQualityMonitor.{check,state,reset}',
    );

    return () => {
      clearInterval(id);
      delete (window as any).__networkQualityMonitor;
    };
  }, [intervalMs, maxNotifications]);

  return (
    <AlertModal
      open={open}
      header={header}
      bodyTxt={bodyTxt}
      buttonText="OK"
      handleClose={() => setOpen(false)}
      handleConfirmation={() => setOpen(false)}
    />
  );
}

export default NetworkQualityMonitor;
